import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  RadioGroup,
  Radio,
  HStack,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useCreatePlaylist } from '../hooks/usePlaylists';
import type { AudioPlaylistDto, PlaylistType } from '../types';

const USE_CASES = ['cafe', 'restaurant', 'bar', 'shop', 'hotel', 'gym', 'spa', 'office', 'other'];

interface CreatePlaylistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (playlist: AudioPlaylistDto) => void;
}

export function CreatePlaylistModal({ isOpen, onClose, onCreated }: CreatePlaylistModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const create = useCreatePlaylist();

  const [name, setName] = useState('');
  const [type, setType] = useState<PlaylistType>('music');
  const [description, setDescription] = useState('');
  const [useCase, setUseCase] = useState('');

  const reset = () => {
    setName('');
    setType('music');
    setDescription('');
    setUseCase('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    try {
      const playlist = await create.mutateAsync({
        name: name.trim(),
        playlistType: type,
        description: description.trim() || undefined,
        targetUseCase: useCase || undefined,
      });
      toast({ status: 'success', title: t('playlists.created'), duration: 2500 });
      reset();
      onCreated(playlist);
    } catch (e) {
      toast({
        status: 'error',
        title: t('common.error', 'Something went wrong'),
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('playlists.create')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>{t('playlists.type')}</FormLabel>
              <RadioGroup value={type} onChange={(v) => setType(v as PlaylistType)}>
                <HStack spacing={6}>
                  <Radio value="music" colorScheme="brand">
                    {t('playlists.typeMusic')}
                  </Radio>
                  <Radio value="advertisement" colorScheme="brand">
                    {t('playlists.typeAd')}
                  </Radio>
                </HStack>
              </RadioGroup>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>{t('playlists.name')}</FormLabel>
              <Input
                value={name}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('playlists.namePlaceholder')}
                autoFocus
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t('playlists.description')}</FormLabel>
              <Textarea
                value={description}
                maxLength={1000}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t('playlists.useCase')}</FormLabel>
              <Select
                placeholder={t('playlists.useCaseNone')}
                value={useCase}
                onChange={(e) => setUseCase(e.target.value)}
              >
                {USE_CASES.map((u) => (
                  <option key={u} value={u}>
                    {t(`playlists.useCases.${u}`)}
                  </option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleSubmit}
            isLoading={create.isPending}
            isDisabled={!name.trim()}
          >
            {t('playlists.create')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CreatePlaylistModal;
